/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { clone, equal } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService } from "@sa/rep-mdm";
import { getCalendarMark } from "./utils/getDefaultMark";
export interface Parameters {
  commodities: number;
  calendar: number;
  refYear: number;
  cbotDate: number;
  units: number[];
}

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  public loading$ = new BehaviorSubject<boolean>(true);
  public olapResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);
  public calendarMark$ = new BehaviorSubject<number[]>([]);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(
    undefined,
  );
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters.pipe(debounceTime(400)).subscribe(async (parameters) => {
      if (parameters) {
        this.readyState = ComponentContextReadyState.Loading;
        this.loading$.next(true);
        this.olapResult$.next(null);

        const dictionariesService = this.options.serviceProvider.resolve<DictionariesService>(
          ServicesRegister.Dictionary,
        );
        const { calendar: calendarMark } = await getCalendarMark(
          dictionariesService,
          parameters.commodities,
          parameters.calendar,
        );
        this.calendarMark$.next(calendarMark);

        const olapResult = await this.query(parameters);

        this.olapResult$.next(olapResult);
        this.readyState = ComponentContextReadyState.Loaded;
        this.loading$.next(false);
      }
    });
  }

  private async _initDefaultMark() {
    const { dataflowService, serviceProvider } = this.options;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const commoditiesParam = dataflowService.getParameter("@commodities") as SingleValueParameter;
    const unitsParam = dataflowService.getParameter("@units") as MultiValueParameter;
    const refYearParam = dataflowService.getParameter("@ref_year") as SingleValueParameter;
    const cbotDateParam = dataflowService.getParameter("@cbot_date") as SingleValueParameter;

    this._parameters.next({
      commodities: commoditiesParam.value as number,
      units: unitsParam.value as number[],
      refYear: refYearParam.value as number,
      cbotDate: cbotDateParam.value as number,
      calendar: (calendarParam.value as number[])[0],
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const commodities = command.values.get("@commodities") as number;
    const calendar = command.values.get("@calendar") as number[];
    const units = command.values.get("@units") as number[];
    const refYear = command.values.get("@ref_year") as number;
    const cbotDate = command.values.get("@cbot_date") as number;

    if (this._parameters.value !== undefined) {
      const parameters = clone(this._parameters.value);
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }
      if (units !== undefined) {
        parameters.units = units;
      }
      if (calendar !== undefined) {
        parameters.calendar = calendar[0];
      }
      if (refYear !== undefined) {
        parameters.refYear = refYear;
      }
      if (cbotDate !== undefined) {
        parameters.cbotDate = cbotDate;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters) {
    const { commodities, calendar, units, refYear, cbotDate } = parameters;

    const calendarMark = this.calendarMark$.value;

    const cubeSlideDef = await dataService.getCubeDefinition(`slide_34`);
    const cubeParameters = clone(cubeSlideDef.parameters);

    cubeParameters["p_commodity_id"]!["defaultValue"] = commodities;
    cubeParameters["p_calendar_id"]!["defaultValue"] = calendar;
    cubeParameters["p_ref_year"]!["defaultValue"] = refYear;
    cubeParameters["p_cbot_date"]!["defaultValue"] = cbotDate;

    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = {
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendarMark,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: [commodities], selectionMode: SelectionMode.Include },
          { path: "units.default", selection: units, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "ending_stocks", aggregationType: AggregationType.None },
              { id: "historical_price", aggregationType: AggregationType.None },
              { id: "fc_price", aggregationType: AggregationType.None },
              { id: "cbot_price", aggregationType: AggregationType.None },
            ],
          },
        ].filter(Boolean),
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any;

    const olapResult = await cubeSlide.query(olapQuery);

    const dataSource = await this._getFormattedSources(commodities);
    this.sources$.next(dataSource);
    return olapResult;
  }

  private async _getFormattedSources(commodities: number): Promise<string> {
    const { dataSourcesService } = this.options;

    const dataSourceHistoricalPrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities, (item) => {
      return item.getValue<string>("comment") === "Historical";
    });
    const dataSourceForecastPrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities, (item) => {
      return item.getValue<string>("comment") === "Forecast";
    });
    const dataSourceEndingStocksPrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities, (item) => {
      return item.getValue<string>("comment") === "Ending stocks";
    });

    const historicalSource = await dataSourceHistoricalPrms;
    const forecastlSource = await dataSourceForecastPrms;
    const endingStocksSource = await dataSourceEndingStocksPrms;

    const sources: string[] = [];
    if (historicalSource) {
      sources.push("Ending stocks - " + endingStocksSource);
    }
    if (forecastlSource) {
      sources.push("Historical - " + historicalSource);
    }
    if (endingStocksSource) {
      sources.push("Forecast  - " + forecastlSource);
    }
    return sources.join("; ");
  }
}
